import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Check, Loader, ExternalLink } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { getWebOrigin } from '../../utils/helpers';

export default function ImportFromPdfModal({ isOpen, onClose, onImport }) {
  const { theme } = useTheme();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // 'upload' | 'parsing' | 'preview'
  const [parsedGroups, setParsedGroups] = useState([]); // [{ fileName, location, permitNumber, events }]
  const [flatEvents, setFlatEvents] = useState([]);     // [{ subject, address, date, startTime, endTime, groupIndex }]
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [importing, setImporting] = useState(false);
  const [parseProgress, setParseProgress] = useState({ done: 0, total: 0 });

  const reset = () => {
    setStep('upload');
    setParsedGroups([]);
    setFlatEvents([]);
    setSelectedEvents([]);
    setImporting(false);
    setParseProgress({ done: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // iOS WebKit applies UTF-8 decoding to files from temp:// paths, producing chars > 255
  // that cause Capacitor's btoa() to throw. ArrayBuffer reads raw bytes; the recreated
  // in-memory File avoids the text-decoding path so readAsBinaryString stays in 0-255.
  const normalizeFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(new File([e.target.result], file.name, { type: file.type || 'application/pdf' }));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  const parseSinglePdf = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${getWebOrigin()}/exportsapi/events/parse-pdf`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Failed to parse ${file.name}`);
    }
    return res.json();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const invalidFiles = files.filter(f => f.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.map(f => f.name).join(', ')} — only PDF files are supported`);
      return;
    }

    setStep('parsing');
    setParseProgress({ done: 0, total: files.length });

    // Parse all PDFs; collect results and errors independently
    const results = await Promise.allSettled(
      files.map(async (file) => {
        const safeFile = await normalizeFile(file);
        const data = await parseSinglePdf(safeFile);
        setParseProgress(p => ({ ...p, done: p.done + 1 }));
        return { fileName: file.name, pdfKey: data.pdfKey || null, ...data };
      })
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
    const failures = results
      .map((r, i) => r.status === 'rejected' ? { name: files[i].name, reason: r.reason?.message } : null)
      .filter(Boolean);

    if (failures.length > 0) {
      failures.forEach(({ name, reason }) => {
        const msg = reason || 'Failed to parse';
        console.error(`[PDF parse] ${name}:`, msg);
        toast.error(`${name}: ${msg}`, { duration: 6000 });
      });
    }

    if (succeeded.length === 0) {
      reset();
      return;
    }

    // Flatten events, tagging each with its group index
    const groups = succeeded;
    const flat = [];
    groups.forEach((group, groupIndex) => {
      (group.events || []).forEach(event => {
        flat.push({ ...event, address: group.location, groupIndex, permitUrl: group.pdfKey || null });
      });
    });

    setParsedGroups(groups);
    setFlatEvents(flat);
    setSelectedEvents(flat.map((_, i) => i)); // select all by default
    setStep('preview');
  };

  const toggleEvent = (index) => {
    setSelectedEvents(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const toggleAll = () => {
    if (selectedEvents.length === flatEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(flatEvents.map((_, i) => i));
    }
  };

  const handleImport = async () => {
    const eventsToImport = flatEvents
      .filter((_, i) => selectedEvents.includes(i))
      .map(e => ({
        subject: e.subject,
        address: e.address,
        date: e.date,
        startTime: e.startTime,
        endTime: e.endTime,
        permitUrl: e.permitUrl || null
      }));

    if (eventsToImport.length === 0) {
      toast.error('Please select at least one event');
      return;
    }

    setImporting(true);
    try {
      await onImport(eventsToImport);
      toast.success(`${eventsToImport.length} event${eventsToImport.length > 1 ? 's' : ''} created`);
      handleClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create events');
    } finally {
      setImporting(false);
    }
  };

  const formatTime = (time24) => {
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-2xl p-6 w-full max-w-lg max-h-[90vh] flex flex-col`}>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-semibold ${theme.text}`}>Import Events from PDF</h3>
          <button onClick={handleClose} className={`p-1 rounded-lg ${theme.cardHover}`}>
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="flex-1 flex flex-col items-center justify-center py-8">
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`w-full border-2 border-dashed ${theme.border} rounded-xl p-10 flex flex-col items-center cursor-pointer hover:${theme.backgroundSecondary} transition-colors`}
            >
              <Upload className={`w-12 h-12 ${theme.textMuted} mb-3`} />
              <p className={`font-medium ${theme.text}`}>Click to upload permit PDFs</p>
              <p className={`text-sm ${theme.textMuted} mt-1`}>Court booking permits, facility reservations, etc.</p>
              <p className={`text-xs ${theme.textMuted} mt-2`}>You can select multiple PDFs at once</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {/* Step: Parsing */}
        {step === 'parsing' && (
          <div className="flex-1 flex flex-col items-center justify-center py-8 gap-4">
            <Loader className={`w-10 h-10 ${theme.textMuted} animate-spin`} />
            <p className={`${theme.text} font-medium`}>Parsing PDF{parseProgress.total > 1 ? 's' : ''} with Claude AI...</p>
            {parseProgress.total > 1 && (
              <p className={`text-sm ${theme.textMuted}`}>
                {parseProgress.done} of {parseProgress.total} done
              </p>
            )}
            <p className={`text-sm ${theme.textMuted}`}>Extracting booking dates and times</p>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && parsedGroups.length > 0 && (
          <>
            {/* Select all / count */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className={`text-sm font-medium ${theme.text}`}>
                {flatEvents.length} booking{flatEvents.length !== 1 ? 's' : ''} found
                {parsedGroups.length > 1 && ` across ${parsedGroups.length} PDFs`}
              </span>
              <button
                onClick={toggleAll}
                className={`text-sm ${theme.link || 'text-blue-500'} hover:underline`}
              >
                {selectedEvents.length === flatEvents.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            {/* Event list grouped by PDF */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4">
              {parsedGroups.map((group, groupIndex) => {
                const groupEvents = flatEvents
                  .map((e, i) => ({ ...e, flatIndex: i }))
                  .filter(e => e.groupIndex === groupIndex);

                return (
                  <div key={groupIndex}>
                    {/* PDF source header */}
                    <div className={`${theme.backgroundSecondary} rounded-lg p-3 mb-2`}>
                      <div className="flex items-center gap-2">
                        <FileText className={`w-4 h-4 ${theme.textMuted} flex-shrink-0`} />
                        <span className={`text-sm font-medium ${theme.text} truncate flex-1`}>{group.location}</span>
                        {group.pdfKey && (
                          <a
                            href={`${getWebOrigin()}/permits/${group.pdfKey}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1 text-xs text-blue-500 hover:underline flex-shrink-0"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            View PDF
                          </a>
                        )}
                      </div>
                      {group.permitNumber && (
                        <p className={`text-xs ${theme.textMuted} mt-1 ml-6`}>Permit #{group.permitNumber}</p>
                      )}
                      {parsedGroups.length > 1 && (
                        <p className={`text-xs ${theme.textMuted} mt-0.5 ml-6 truncate`}>{group.fileName}</p>
                      )}
                    </div>

                    {/* Events for this PDF */}
                    <div className="space-y-2">
                      {groupEvents.map(({ flatIndex, subject, date, startTime, endTime }) => {
                        const selected = selectedEvents.includes(flatIndex);
                        return (
                          <div
                            key={flatIndex}
                            onClick={() => toggleEvent(flatIndex)}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selected
                                ? `border-blue-500 ${theme.backgroundSecondary}`
                                : `${theme.border} hover:${theme.backgroundSecondary}`
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              selected ? 'bg-blue-500 border-blue-500' : theme.border
                            }`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${theme.text}`}>{subject}</p>
                              <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                                {formatDate(date)} · {formatTime(startTime)} – {formatTime(endTime)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={importing}
                className={`flex-1 px-4 py-2 rounded-lg ${theme.cardHover} ${theme.text} disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || selectedEvents.length === 0}
                className={`flex-1 px-4 py-2 rounded-lg ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {importing ? (
                  <><Loader className="w-4 h-4 animate-spin" /> Creating...</>
                ) : (
                  `Import ${selectedEvents.length} Event${selectedEvents.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
